"use client";

import { c as _c } from "react/compiler-runtime";
import type Link from "next/link";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { type ComponentProps, type Dispatch, type PropsWithChildren, type SetStateAction, Suspense, createContext, useCallback, useContext, useEffect, useState } from "react";
import { readonlyURLSearchParamsToStr } from "@/lib/type-safe-stringify";
import { cn } from "@/lib/utils";
import { HoverPrefetchLink } from "./hover-perfetch-link";
type RouteLoaderContext = [boolean, Dispatch<SetStateAction<boolean>>];
const RouteLoaderContext = createContext<RouteLoaderContext>([false, () => {
  throw new Error("setIsRouteLoading not initialized");
}]);
export function RouteLoader(t0) {
  const $ = _c(4);
  const {
    children
  } = t0;
  const t1 = useState(false);
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <Suspense fallback={<div className="h-0.5 w-full" />}><Loader /></Suspense>;
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  let t3;
  if ($[1] !== children || $[2] !== t1) {
    t3 = <RouteLoaderContext.Provider value={t1}>{t2}{children}</RouteLoaderContext.Provider>;
    $[1] = children;
    $[2] = t1;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  return t3;
}
function Loader() {
  const $ = _c(10);
  const [isRouteLoading, setIsRouteLoading] = useContext(RouteLoaderContext);
  const pathname = usePathname();
  const params = useSearchParams();
  let t0;
  if ($[0] !== setIsRouteLoading) {
    t0 = () => {
      setIsRouteLoading(false);
    };
    $[0] = setIsRouteLoading;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== params || $[3] !== pathname || $[4] !== setIsRouteLoading) {
    t1 = [pathname, params, setIsRouteLoading];
    $[2] = params;
    $[3] = pathname;
    $[4] = setIsRouteLoading;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  useEffect(t0, t1);
  const t2 = isRouteLoading && "animate-[route-loader-grow_60s_cubic-bezier(0.33,1,0.68,1)_forwards]";
  let t3;
  if ($[6] !== t2) {
    t3 = cn("from-brand-yellow to-brand-orange sticky top-0 z-50 h-0.5 w-0 bg-gradient-to-r", t2);
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== t3) {
    t4 = <div className={t3} />;
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  return t4;
}
export function useRouterPushWithLoader() {
  const $ = _c(6);
  const [, setIsRouteLoading] = useContext(RouteLoaderContext);
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  let t0;
  if ($[0] !== searchParams) {
    t0 = readonlyURLSearchParamsToStr(searchParams);
    $[0] = searchParams;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const searchParamsString = t0;
  const currentUrl = searchParamsString === "" ? pathname : `${pathname}?${searchParamsString}`;
  let t1;
  if ($[2] !== currentUrl || $[3] !== router || $[4] !== setIsRouteLoading) {
    t1 = (...t2) => {
      const args = t2;
      setIsRouteLoading(false);
      const [href] = args;
      if (href !== currentUrl) {
        requestAnimationFrame(() => {
          setIsRouteLoading(true);
        });
      }
      router.push(...args);
    };
    $[2] = currentUrl;
    $[3] = router;
    $[4] = setIsRouteLoading;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  return t1;
}
export function LinkWithLoader(props) {
  const $ = _c(6);
  const [, setIsRouteLoading] = useContext(RouteLoaderContext);
  let t0;
  if ($[0] !== props || $[1] !== setIsRouteLoading) {
    t0 = (...t1) => {
      const args = t1;
      props.onClick?.(...args);
      setIsRouteLoading(false);
      requestAnimationFrame(() => {
        setIsRouteLoading(true);
      });
    };
    $[0] = props;
    $[1] = setIsRouteLoading;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  let t1;
  if ($[3] !== props || $[4] !== t0) {
    t1 = <HoverPrefetchLink {...props} onClick={t0} />;
    $[3] = props;
    $[4] = t0;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  return t1;
}